'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function StudyPlanCapture() {
  const [name, setName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examType, setExamType] = useState('AP Bio');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !examDate) return;
    setLoading(true);
    // Simulate plan generation
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1800);
  };

  const daysUntil = examDate
    ? Math.max(0, Math.ceil((new Date(examDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <section id="plan" className="py-28 px-6 md:px-12 bg-ink">
      <div className="max-w-2xl mx-auto text-center">
        <span className="text-xs font-semibold tracking-widest uppercase text-amber block mb-4">
          Your Study Plan
        </span>
        <h2 className="font-fraunces text-5xl md:text-6xl font-light text-cream mb-6 leading-tight">
          Tell us when.<br />
          <span className="italic text-amber">We'll map the path.</span>
        </h2>
        <p className="text-cream/60 font-light text-lg mb-12 leading-relaxed">
          Give us your name and exam date. We'll build a day-by-day prep calendar that hits your gap zones first and saves your mastery zones for review week.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="bg-cream/5 border border-cream/10 rounded-3xl p-8 text-left">
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-cream/50 uppercase tracking-widest mb-2">
                  Your name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="First name is fine"
                  className="w-full bg-cream/10 border border-cream/15 rounded-2xl px-5 py-3.5 text-cream placeholder-cream/30 text-sm focus:outline-none focus:border-amber transition-colors"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-cream/50 uppercase tracking-widest mb-2">
                  Exam type
                </label>
                <div className="flex gap-3 flex-wrap">
                  {['AP Bio', 'MCAT Bio', 'NCLEX Bio', 'Other'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setExamType(type)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        examType === type
                          ? 'bg-amber text-ink' :'bg-cream/10 text-cream/60 border border-cream/15 hover:border-amber/50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-cream/50 uppercase tracking-widest mb-2">
                  Exam date
                </label>
                <input
                  type="date"
                  value={examDate}
                  onChange={e => setExamDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-cream/10 border border-cream/15 rounded-2xl px-5 py-3.5 text-cream text-sm focus:outline-none focus:border-amber transition-colors"
                  required
                />
                {daysUntil !== null && (
                  <p className="text-xs text-amber mt-2 font-medium">
                    {daysUntil} days to prepare — {daysUntil >= 30 ? 'plenty of time' : daysUntil >= 14 ? 'tight but doable' : 'crunch mode: focus on gaps only'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-amber w-full py-4 rounded-2xl text-base flex items-center justify-center gap-3 mt-2 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-ink/30 border-t-ink rounded-full animate-spin" />
                    Building your calendar...
                  </>
                ) : (
                  <>
                    <Icon name="CalendarDaysIcon" size={18} />
                    Generate My Study Plan
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-green/15 border border-green/30 rounded-3xl p-10 text-center animate-scale-in">
            <div className="w-16 h-16 rounded-full bg-green flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckIcon" size={28} className="text-white" variant="solid" />
            </div>
            <h3 className="font-fraunces text-3xl font-medium text-cream mb-3">
              {name}'s plan is ready.
            </h3>
            <p className="text-cream/60 font-light mb-6">
              Your {examType} prep calendar has been generated. {daysUntil !== null && `${daysUntil} days, sequenced by your topic map.`}
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { label: 'Gap topics', value: 'First 40%', color: 'text-mauve' },
                { label: 'Review zones', value: 'Middle 40%', color: 'text-amber' },
                { label: 'Mastery review', value: 'Final 20%', color: 'text-green-light' },
              ].map(item => (
                <div key={item.label} className="bg-cream/5 rounded-2xl p-4">
                  <p className={`text-lg font-fraunces font-semibold ${item.color}`}>{item.value}</p>
                  <p className="text-xs text-cream/50 mt-1">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-cream/40 font-light">
              Check your email for the full calendar PDF. Or start drilling now — your weakest topics are queued.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}